import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { Toaster } from 'sonner';
import './App.css';
import './print.css';

// Components
import SubscriptionGuard from './components/SubscriptionGuard';

// Layout
import MainLayout from './components/Layout/MainLayout';

// Pages
import Login from './pages/Login';
import InstitutionSignup from './pages/InstitutionSignup';
import Dashboard from './pages/Dashboard';
import PatientList from './pages/Patients/PatientList';
import PatientRegister from './pages/Patients/PatientRegister';
import PatientDetail from './pages/Patients/PatientDetail';
import AdmissionList from './pages/Admissions/AdmissionList';
import PrescriptionList from './pages/Prescriptions/PrescriptionList';
import ProcedureList from './pages/Procedures/ProcedureList';
import AppointmentList from './pages/Appointments/AppointmentList';
import LabTestList from './pages/LabTests/LabTestList';
import ImagingTestList from './pages/ImagingTests/ImagingTestList';
import PharmacyDispense from './pages/Pharmacy/PharmacyDispense';
import StockManagement from './pages/Stock/StockManagement';
import BillingList from './pages/Billing/BillingList';
import ReportsPage from './pages/Reports/ReportsPage';
import UserManagement from './pages/Users/UserManagement';
import LeaveManagement from './pages/Leave/LeaveManagement';
import MedicationApprovals from './pages/Approvals/MedicationApprovals';
import CaseMessages from './pages/Messages/CaseMessages';
import PharmacyQueue from './pages/Workflow/PharmacyQueue';
import LabQueue from './pages/Workflow/LabQueue';
import ImagingQueue from './pages/Workflow/ImagingQueue';
import ReceptionClosure from './pages/Workflow/ReceptionClosure';
import PricingMaster from './pages/Settings/PricingMaster';
import UserSettings from './pages/Settings/UserSettings';
import RefereeManagement from './pages/Settings/RefereeManagement';
import InstitutionSettings from './pages/Settings/InstitutionSettings';
import InvoiceSettings from './pages/Settings/InvoiceSettings';
import NotificationPreferences from './pages/Settings/NotificationPreferences';
import NotificationHistory from './pages/Settings/NotificationHistory';
import NotificationAnalytics from './pages/Settings/NotificationAnalytics';
import WebhookSettings from './pages/Settings/WebhookSettings';
import PlatformDashboard from './pages/Platform/PlatformDashboard';
import PlatformAnalytics from './pages/Platform/PlatformAnalytics';
import AlertThresholds from './pages/Platform/AlertThresholds';
import MLInsights from './pages/Platform/MLInsights';
import RolesDepartments from './pages/Platform/RolesDepartments';
import PendingContributions from './pages/Platform/PendingContributions';
import MasterDataManager from './pages/Platform/MasterDataManager';
import SubscriptionPage from './pages/Subscription/SubscriptionPage';
import PricingPage from './pages/Subscription/PricingPage';
import PatientSearch from './pages/Patients/PatientSearch';
import SupportInbox from './pages/Support/SupportInbox';
import SuggestedItems from './pages/Platform/SuggestedItems';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/institution-signup"
        element={
          <PublicRoute>
            <InstitutionSignup />
          </PublicRoute>
        }
      />
      <Route
        path="/pricing"
        element={<PricingPage />}
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <MainLayout />
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        
        {/* Patient Management */}
        <Route path="patients" element={<PatientList />} />
        <Route path="patients/register" element={<PatientRegister />} />
        <Route path="patients/search" element={<PatientSearch />} />
        <Route path="patients/:id" element={<PatientDetail />} />
        
        {/* Admissions */}
        <Route path="admissions" element={<AdmissionList />} />
        
        {/* Clinical */}
        <Route path="prescriptions" element={<PrescriptionList />} />
        <Route path="procedures" element={<ProcedureList />} />
        <Route path="appointments" element={<AppointmentList />} />
        
        {/* Diagnostics */}
        <Route path="lab-tests" element={<LabTestList />} />
        <Route path="imaging-tests" element={<ImagingTestList />} />
        
        {/* Pharmacy */}
        <Route path="pharmacy" element={<PharmacyDispense />} />
        <Route path="stock" element={<StockManagement />} />
        
        {/* Billing */}
        <Route path="billing" element={<BillingList />} />
        <Route path="reports" element={<ReportsPage />} />
        
        {/* Communication */}
        <Route path="messages" element={<CaseMessages />} />
        
        {/* Workflow */}
        <Route path="workflow/pharmacy-queue" element={<PharmacyQueue />} />
        <Route path="workflow/lab-queue" element={<LabQueue />} />
        <Route path="workflow/imaging-queue" element={<ImagingQueue />} />
        <Route path="workflow/reception-closure" element={<ReceptionClosure />} />
        
        {/* Approvals */}
        <Route path="medication-approvals" element={<MedicationApprovals />} />
        <Route path="leave" element={<LeaveManagement />} />
        
        {/* Administration */}
        <Route path="users" element={<UserManagement />} />
        <Route path="settings/institution" element={<InstitutionSettings />} />
        <Route path="settings/pricing-master" element={<PricingMaster />} />
        <Route path="settings/invoice" element={<InvoiceSettings />} />
        <Route path="settings/referees" element={<RefereeManagement />} />
        <Route path="settings/profile" element={<UserSettings />} />
        <Route path="settings/notifications" element={<NotificationPreferences />} />
        <Route path="settings/notifications/history" element={<NotificationHistory />} />
        <Route path="settings/notifications/analytics" element={<NotificationAnalytics />} />
        <Route path="settings/webhooks" element={<WebhookSettings />} />
        
        {/* Platform Admin */}
        <Route path="platform" element={<PlatformDashboard />} />
        <Route path="platform/analytics" element={<PlatformAnalytics />} />
        <Route path="platform/alert-thresholds" element={<AlertThresholds />} />
        <Route path="platform/ml-insights" element={<MLInsights />} />
        <Route path="platform/roles-departments" element={<RolesDepartments />} />
        <Route path="platform/pending-contributions" element={<PendingContributions />} />
        <Route path="platform/master-data" element={<MasterDataManager />} />
        <Route path="platform/suggested-items" element={<SuggestedItems />} />
        
        {/* Support */}
        <Route path="support/inbox" element={<SupportInbox />} />
        
        {/* Subscription */}
        <Route path="subscription" element={<SubscriptionPage />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <WebSocketProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </WebSocketProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
